import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import * as Util from '../../../common/utilities';

import {
  Button,
  Card,
  DataTable,
  DataTableColumn,
  DataTableCell,
  Dropdown,
  Icon,
  Popover,
  Tooltip
} from '@salesforce/design-system-react';

import { withFlags } from '../../../flags/Flags';
import { Dot, TruncatedList } from '../../../common/components/';

import './AssetBrowser.scss';
import formatTimestamp from '../../../i18n/formatTimestamp';

const CustomDataTableCell = ({ children, property, ...props }) => {
  const { formatMessage } = props.intl;
  const formatDate = formatTimestamp(props.intl);
  const isParent = props.item.child_ids?.length;
  const isChild = !!props.item.parent_id;
  const cellClasses = [];
  if (props.item.highlight) {
    cellClasses.push('highlight-cell');
  } else if (isChild) {
    cellClasses.push('child-cell');
  }

  const cellClass = cellClasses.join(' ').trim();

  switch (property) {
    case 'expand':
      const expanded = props.expandedRows.includes(props.item.uuid);
      const icon = expanded ? 'chevrondown' : 'chevronright';
      if (isParent) {
        const chevronClasses = ['chevron-cell is-parent'];
        if (props.item.highlight) {
          chevronClasses.push('highlight-cell');
        }
        return (
          <td
            onClick={() => props.onAction('toggleExpand', props.item)}
            className={chevronClasses.join(' ').trim()}
          >
            <Icon category="utility" name={icon} size="xx-small" />
          </td>
        );
      } else {
        return <td className={`chevron-cell ${cellClass}`} />;
      }
    case 'dot':
      return (
        <td className={`${cellClass}`}>
          <Dot colorIdx={props.item.dot} />
        </td>
      );
    case 'actions':
      const isAvailable = props.item.availability === 'Y';
      const toggle = isAvailable ? 'disable' : 'enable';
      const disabled =
        props.item.fallback === 'Y' &&
        isAvailable &&
        props.assetClassesMap.get(props.item.asset_class_id) === 1;
      // The tooltip needs undefined for isOpen value, in order to open on hover
      const open = disabled ? undefined : false;
      const actions = [
        {
          label: formatMessage({ id: 'components.ecs.assetBrowser.menu.edit' }),
          value: 'edit'
        },
        {
          label: formatMessage({
            id: `components.ecs.assetBrowser.menu.${toggle}`
          }),
          value: toggle
        }
      ];

      if (!props.item.parent_id) {
        const archiveType = props.item.archived ? 'unarchive' : 'archive';
        actions.push({
          label: formatMessage({
            id: `components.ecs.assetBrowser.menu.${archiveType}`
          }),
          value: archiveType
        });
      }

      return (
        <td className={`${cellClass}`}>
          <Dropdown
            id="asset-options-dropdown"
            assistiveText={{ icon: 'Asset Options' }}
            buttonVariant="icon"
            iconCategory="utility"
            iconName="down"
            iconVariant="border-filled"
            align="right"
            className="actions-dropdown"
            width="xx-small"
            onSelect={({ value }) =>
              props.onAction(value, props.item, disabled)
            }
            options={actions}
            tooltipMenuItem={
              <Tooltip
                id="drop-down-tooltip"
                align="left"
                position="overflowBoundaryElement"
                isOpen={open}
              />
            }
          />
        </td>
      );
    case 'key':
      // Link to selection details from Selection Browser
      if (props.item.clicked !== undefined) {
        return (
          <td className={`wrap-text asset-name ${cellClass}`}>
            <Button
              className="asset-details-link"
              label={children}
              variant="base"
              onClick={() => props.onAction('openDetails', props.item)}
            />
          </td>
        );
      }
      return (
        <td className={`wrap-text asset-name ${cellClass}`}>{children}</td>
      );
    case 'ect_tags':
      if (children === null) {
        return <td className={cellClass} />;
      }

      const selected_tags = children
        .filter(tag => tag.selected)
        .map(tag => tag.description);

      return (
        <td className={`wrap-text ${cellClass}`}>
          {selected_tags.length !== 0 && <TruncatedList list={selected_tags} />}
        </td>
      );
    case 'thumbnail':
      const rowNumber = props.items.indexOf(props.item);
      let popoverAlign = 'right top';
      let position = 'relative';
      if (props.items.length - rowNumber < 5) {
        popoverAlign = 'right bottom';
        position = 'absolute';
      }
      // Empty pixel or Contingency has no Popover
      if (props.item.uuid === '0') {
        return (
          <td className={`${cellClass}`}>
            <Button variant="base">
              <div
                className="thumbnail-container"
                style={{ backgroundImage: `url(${props.item.image_link})` }}
              />
            </Button>
          </td>
        );
      }
      return (
        <td className={`thumbnail-cell ${cellClass}`}>
          <Popover
            className={`thumbnail-expanded-popover ${
              props.popoverOverflow ? 'fallback-wizard' : ''
            }`}
            heading={props.item.key}
            body={
              <div className="popover-body">
                <img
                  className="thumbnail-cell-popover-img"
                  src={props.item.image_link}
                  alt={formatMessage(
                    {
                      id: 'components.ecs.popover.image.altText'
                    },
                    {
                      name: props.item.key
                    }
                  )}
                />
                <div className="popover-text slds-grid slds-gutters">
                  <span className="popover-label slds-col slds-size_1-of-2">
                    {formatMessage({
                      id: 'components.ecs.popover.image.assetName'
                    })}
                  </span>
                  <span className="popover-value slds-col slds-size_2-of-2">
                    {props.item.asset.key}
                  </span>
                </div>
                <div className="popover-text slds-grid slds-gutters">
                  <span className="popover-label slds-col slds-size_1-of-2">
                    {formatMessage({
                      id: 'components.ecs.popover.image.assetClass'
                    })}
                  </span>
                  <span className="popover-value slds-col slds-size_2-of-2">
                    {props.item.asset.type}
                  </span>
                </div>
              </div>
            }
            align={popoverAlign}
            position={
              props.popoverOverflow ? 'overflowBoundaryElement' : position
            }
          >
            <Button variant="base">
              <div
                className="thumbnail-container"
                style={{ backgroundImage: `url(${props.item.image_link})` }}
              />
            </Button>
          </Popover>
        </td>
      );
    case 'availability':
    case 'fallback':
      const i18nKey = `components.ecs.assetFilter.${children}`;
      return (
        <td className={`${cellClass}`}>
          <FormattedMessage id={i18nKey} />
        </td>
      );
    case 'image_link':
    case 'url':
      return (
        <td className={`wrap-text asset-url ${cellClass}`}>
          <a
            href={children}
            target="_blank"
            rel="noopener noreferrer"
            title={children}
          >
            <span className="truncate">{children}</span>
          </a>
        </td>
      );
    case 'start_datetime':
    case 'end_datetime':
      const tzName = props.item.display_time_zone_name || 'Etc/UTC';
      return (
        <td className={`wrap-text ${cellClass}`}>
          {children ? formatDate(children, undefined, tzName) : ''}
        </td>
      );
    case 'subject_line':
      return (
        <td className={`wrap-text ${cellClass}`}>
          <span className="truncate" title={children}>
            {children}
          </span>
        </td>
      );
    case 'preheader':
      return (
        <td className={`wrap-text ${cellClass}`}>
          <span className="truncate">{children}</span>
        </td>
      );
    case 'clicked':
    case 'isFallback':
      const badge = children ? 'slds-theme_success' : 'slds-theme_default';
      const i18id = children ? 'common.yes' : 'common.no';
      const text = props.formatMessage({ id: i18id });
      // Empty pixel or Contingency has no click data
      if (props.item.uuid === '0') {
        return <td className={`${cellClass}`} />;
      }
      return (
        <td className={`${cellClass}`}>
          <span className={`job-badge-cell slds-badge ${badge}`}>{text}</span>
        </td>
      );
    default: {
      let val = children;
      const weatherAttributes = props.attributes?.weatherAttributes || [];
      if (weatherAttributes.length) {
        const weatherKeys = (props.attributes?.weatherAttributes || []).map(
          attr => attr.name
        );
        if (weatherKeys.includes(property)) {
          const attr = weatherAttributes.find(attr => attr.name === property);
          if (Array.isArray(val) && val.length > 0) {
            const weatherType =
              (attr.type === 'WeatherAlarmAttribute' && 'alarmTypes') ||
              (attr.type === 'WeatherIndexAttribute' && 'indexCategories') ||
              '';
            if (weatherType.length > 0) {
              val = val.map(value =>
                formatMessage({
                  id: `components.ecs.attributes.modal.weather.${weatherType}.${value}`
                })
              );
            }
          }
        }
      }
      if (props.numberStyle) {
        const { formatNumber } = props.intl;
        val = formatNumber(children, props.numberStyle);
      }
      if (Array.isArray(val) && val.length > 1) {
        return (
          <td className={`wrap-text ${cellClass}`}>
            <TruncatedList list={val} />
          </td>
        );
      } else {
        return <td className={`wrap-text ${cellClass}`}>{val}</td>;
      }
    }
  }
};

CustomDataTableCell.displayName = DataTableCell.displayName;
CustomDataTableCell.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object,
    PropTypes.array
  ]),
  property: PropTypes.string.isRequired,
  onclick: PropTypes.func,
  onAction: PropTypes.func,
  formatMessage: PropTypes.func,
  item: PropTypes.object,
  numberStyle: PropTypes.object,
  intl: intlShape
};

class AssetBrowser extends Component {
  constructor(props) {
    super(props);
    this.assetTable = React.createRef();
    this.state = { horizontalScroll: false };
  }

  headerCell(property, isCustom, formatMessage, tooltip) {
    const text = isCustom
      ? property
      : formatMessage({ id: `components.ecs.assetBrowser.${property}` });
    if (tooltip) {
      const tooltipText = formatMessage({ id: tooltip });
      if (tooltip.endsWith('weather.tooltip')) {
        return (
          <span>
            {text}
            <Tooltip
              align="right"
              content={tooltipText}
              position="overflowBoundaryElement"
            >
              <Button
                assistiveText={{ icon: 'Icon Bare Small' }}
                iconCategory="custom"
                iconName="custom60"
                iconSize="medium"
                iconVariant="bare"
                variant="icon"
              />
            </Tooltip>
          </span>
        );
      }
      return (
        <span>
          {text}
          <Tooltip
            align="right"
            content={tooltipText}
            position="overflowBoundaryElement"
          />
        </span>
      );
    }

    return text;
  }

  columns(flags) {
    const defaultWidth = '6.25rem';
    const buildColumn = (
      name,
      width,
      custom,
      tooltip,
      sortable,
      sortDirection
    ) => ({
      name,
      sortable: sortable || false,
      sortDirection: sortDirection || undefined,
      width: width || defaultWidth,
      custom,
      tooltip
    });

    const sortDirection = key =>
      this.props.sortColumn?.property === key &&
      this.props.sortColumn?.sortDirection;

    const columns = [];
    if (this.props.columns?.length) {
      columns.push(...this.props.columns);
    } else {
      if (this.props.allowParentExpand) {
        columns.push(buildColumn('expand', '20px'));
      }
      columns.push(buildColumn('thumbnail', '62px'));

      const defaultColumns = [
        'key',
        'type',
        'url',
        'start_datetime',
        'end_datetime',
        'fallback',
        'availability'
      ];
      if (flags.subjectLine) {
        defaultColumns.push('subject_line', 'preheader');
      }
      columns.push(
        ...defaultColumns.map(key => {
          const width = key === 'key' ? '7rem' : undefined;
          return buildColumn(
            key,
            width,
            undefined,
            undefined,
            this.props.sortable || false,
            sortDirection(key)
          );
        })
      );

      if (this.props.rules?.settings?.ect) {
        columns.push(
          buildColumn(
            'ect_tags',
            undefined,
            false,
            'components.ecs.assetBrowser.tags.tooltip'
          )
        );
      }
    }

    if (this.props.keys?.length) {
      const weatherKeys = (this.props.attributes?.weatherAttributes || []).map(
        attr => attr.name
      );
      const customKeys = this.props.keys.map(key => {
        const tooltip = weatherKeys.includes(key)
          ? 'components.ecs.assetBrowser.weather.tooltip'
          : undefined;
        return buildColumn(key, undefined, true, tooltip);
      });
      columns.push(...customKeys);
    }

    if (this.props.showTimestampColumns) {
      ['created_at', 'updated_at'].forEach(key =>
        columns.push(
          buildColumn(
            key,
            undefined,
            undefined,
            undefined,
            this.props.sortable || false,
            sortDirection(key)
          )
        )
      );
    }

    if (this.props.showActions && !(flags && flags.isReadonly)) {
      columns.push(buildColumn('actions', '50px'));
    }
    return columns.reduce((obj, item) => {
      obj[item.name] = item;
      return obj;
    }, {});
  }

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    /**
     * Only update this component if: we've never rendered or one of its props changes
     *
     * This code prevents some major input lag when typing into the AssetEditWizard because
     * every change (ie keypress) in the form would otherwise re-render this whole component
     */
    if (this.hasRendered) {
      const selectionChanged = !_.isEqual(
        this.props.selection,
        nextProps.selection
      );
      const assetsChanged = !_.isEqual(this.props.assets, nextProps.assets);
      const expandChanged = !_.isEqual(
        this.props.expandedRows,
        nextProps.expandedRows
      );
      const assetsClassesChanged = !_.isEqual(
        this.props.assetClasses,
        nextProps.assetClasses
      );
      const scrollChanged = !_.isEqual(
        this.state.horizontalScroll,
        nextState.horizontalScroll
      );
      const ectChanged = !_.isEqual(
        this.props.rules?.settings?.ect,
        nextProps.rules?.settings?.ect
      );
      const scrollToTopChanged =
        this.props.scrollToTop !== nextProps.scrollToTop;
      const highlightChanged =
        this.props.highlightKey !== nextProps.highlightKey;
      return (
        selectionChanged ||
        assetsChanged ||
        expandChanged ||
        scrollChanged ||
        ectChanged ||
        assetsClassesChanged ||
        scrollToTopChanged ||
        highlightChanged
      );
    }
    return true;
  }

  hasHover() {
    let cWidth = this.assetTable.current?.scrollerRef?.clientWidth;
    let sWidth = this.assetTable.current?.scrollerRef?.scrollWidth;
    if (cWidth < sWidth) {
      if (this.state.horizontalScroll !== 'horScroll') {
        this.setState({ horizontalScroll: 'horScroll' });
      }
    } else {
      if (this.state.horizontalScroll !== 'noScroll') {
        this.setState({ horizontalScroll: 'noScroll' });
      }
    }
  }

  componentDidUpdate(prevProps) {
    this.hasHover();
    if (!this.state.listenerAdded) {
      window.addEventListener('resize', this.hasHover.bind(this));
      this.setState({ listenerAdded: true });
    }
    if (Util.propChanged(prevProps, this.props, 'scrollToTop', true)) {
      if (this.assetTable.current?.scrollerRef) {
        this.assetTable.current.scrollerRef.scrollTo({ top: 0 });
        if (this.props.onScrollToTop) {
          this.props.onScrollToTop();
        }
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.hasHover.bind(this));
  }

  tableData() {
    const formatDate = formatTimestamp(this.props.intl);
    const isHighlighted = asset => asset.key === this.props.highlightKey;
    if (this.props.assets) {
      return this.props.assets.map(item => ({
        ...item,
        ...item.custom,
        highlight: isHighlighted(item),
        id: item?.uuid?.toString(),
        created_at: formatDate(item.created_at),
        updated_at: formatDate(item.updated_at),
        asset: { ...item }
      }));
    }
    return [];
  }

  loadMore() {
    if (this.props.onLoadMore) {
      this.props.onLoadMore();
    }
  }

  renderTable(items) {
    const { assetClasses } = this.props;
    const assetClassesMap = new Map();
    if (assetClasses) {
      assetClasses.forEach(assetClass =>
        assetClassesMap.set(assetClass.uuid, assetClass.fallback_count)
      );
    }
    const { formatMessage } = this.props.intl;
    const noHover =
      this.props.className === 'all' ||
      this.props.className === 'overview' ||
      this.props.className === 'selections';

    return withFlags(flags => {
      let selection = this.props.selection;
      if (selection) {
        const selectedKeys = selection.map(s => s.key);
        selection = items.filter(item => selectedKeys.includes(item.key));
      }
      const columns = this.columns(flags);
      const expandClassName = Object.keys(columns).includes('expand')
        ? 'with-expand'
        : 'without-expand';
      const sortFunc = this.props.onSort ? this.props.onSort : () => {};
      return (
        <Card hasNoHeader id="table-wrapper">
          <div
            className={`asset-browser ${this.props.className} ${expandClassName}`}
          >
            <DataTable
              items={items}
              className={`asset-table ${this.state.horizontalScroll}`}
              selection={selection}
              selectRows={this.props.rowSelectionMode}
              onRowChange={this.props.onRowSelect}
              noRowHover={noHover}
              fixedHeader
              fixedLayout
              ref={this.assetTable}
              hasMore={this.props.hasMore}
              onLoadMore={() => this.loadMore()}
              onSort={sortFunc}
            >
              {Object.entries(columns).map((column, index) => {
                const sortable = column[1].sortable;
                const sortDirection = column[1].sortDirection;
                const width = column[1].width;
                const custom = column[1].custom;
                const numberStyle = column[1].numberStyle;
                const tooltip = column[1].tooltip;
                const property = column[0];
                return (
                  <DataTableColumn
                    key={index}
                    sortable={!!sortable}
                    isSorted={!!sortDirection}
                    sortDirection={sortDirection}
                  >
                    <CustomDataTableCell
                      property={property}
                      formatMessage={formatMessage}
                      label={this.headerCell(
                        property,
                        custom,
                        formatMessage,
                        tooltip
                      )}
                      items={items}
                      assetClassesMap={assetClassesMap}
                      attributes={this.props.attributes}
                      width={width}
                      numberStyle={numberStyle}
                      intl={this.props.intl}
                      popoverOverflow={this.props.popoverOverflow}
                      onAction={this.props.onAction}
                      expandedRows={this.props.expandedRows || []}
                      flags={flags}
                    />
                  </DataTableColumn>
                );
              })}
            </DataTable>
          </div>
        </Card>
      );
    });
  }

  render() {
    this.hasRendered = true;
    return this.renderTable(this.tableData());
  }
}

AssetBrowser.defaultProps = {
  className: ''
};

AssetBrowser.propTypes = {
  assets: PropTypes.array,
  className: PropTypes.string,
  allowParentExpand: PropTypes.bool,
  expandedRows: PropTypes.array,
  rowSelectionMode: PropTypes.oneOf(['radio', 'checkbox']),
  selection: PropTypes.array,
  onLoadMore: PropTypes.func,
  onRowSelect: PropTypes.func,
  onAction: PropTypes.func,
  onScrollToTop: PropTypes.func,
  scrollToTop: PropTypes.bool,
  popoverOverflow: PropTypes.bool,
  keys: PropTypes.array,
  attributes: PropTypes.shape({
    assetAttributes: PropTypes.array,
    weatherAttributes: PropTypes.array
  }),
  highlightKey: PropTypes.string,
  sortable: PropTypes.bool,
  sortColumn: PropTypes.object,
  onSort: PropTypes.func,
  showTimestampColumns: PropTypes.bool,
  columns: PropTypes.array,
  rules: PropTypes.object,
  showActions: PropTypes.bool,
  location: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(withRouter(AssetBrowser));
